/**
 * ShareButton — share a pplTalk post via Web Share API or copy link.
 * Public action — no login required.
 */

import React, { useState, useCallback } from 'react';

interface ShareButtonProps {
  /** The URL to share */
  url: string;
  /** The title of the content */
  title: string;
  /** Optional description */
  text?: string;
  /** Size variant */
  size?: 'default' | 'small';
}

const ShareButton: React.FC<ShareButtonProps> = ({ url, title, text, size = 'default' }) => {
  const [copied, setCopied] = useState(false);

  const fullUrl = url.startsWith('http') ? url : `https://pplkpr.ai${url}`;

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Try Web Share API first (mobile browsers, some desktop)
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: text || title,
          url: fullUrl,
        });
        return;
      } catch (err) {
        // User cancelled or API not supported — fall through to clipboard
        if ((err as Error).name === 'AbortError') return;
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Last resort: use textarea hack for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = fullUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [fullUrl, title, text]);

  return (
    <button
      className={`share-button ${size === 'small' ? 'share-button--small' : ''}`}
      onClick={handleShare}
      type="button"
      title={copied ? 'Link copied!' : 'Share'}
      aria-label="Share this post"
    >
      {copied ? (
        <>
          <span className="share-button__icon">✓</span>
          <span className="share-button__text">copied!</span>
        </>
      ) : (
        <>
          <span className="share-button__icon">🔗</span>
          <span className="share-button__text">share</span>
        </>
      )}
    </button>
  );
};

export default ShareButton;
